import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Printer } from "lucide-react";

export default function AdminOrderSlip() {
  const { oid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/orders/${oid}/slip`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load order");
    }
  }, [oid]);

  useEffect(() => { load(); }, [load]);

  if (err) return <div className="p-16 text-zinc-500">{err}</div>;
  if (!data) return <div className="p-16 text-zinc-500">Loading packing slip…</div>;

  const { order, buyer } = data;
  const addr = buyer?.shipping_address || {};
  const prefs = buyer && [
    ["Favorite Player", buyer.favorite_player],
    ["Favorite Team", buyer.favorite_team],
    ["Favorite Sport", buyer.favorite_sport],
    ["Preferred Brand", buyer.preferred_brand],
    ["Card Type", buyer.favorite_card_type],
  ].filter(([, v]) => !!v);

  return (
    <div className="slip-page">
      <style>{`
        .slip-page {
          background: #ffffff;
          color: #111111;
          min-height: 100vh;
          padding: 32px;
          font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
        }
        .slip-max { max-width: 780px; margin: 0 auto; }
        .slip-header { display: flex; justify-content: space-between; align-items: flex-start;
                       padding-bottom: 20px; border-bottom: 2px solid #FF3B30; }
        .slip-brand { font-family: 'Bebas Neue', 'Impact', sans-serif; font-size: 42px; letter-spacing: 1px; color: #111; }
        .slip-brand em { color: #FF3B30; font-style: normal; }
        .slip-meta { text-align: right; font-size: 12px; color: #555; }
        .slip-meta .oid { font-family: monospace; color: #111; font-size: 14px; }
        .slip-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 28px; }
        .slip-label { font-size: 10px; letter-spacing: 3px; text-transform: uppercase; color: #888; margin-bottom: 6px; }
        .slip-box { border: 1px solid #ddd; border-radius: 6px; padding: 16px; }
        table.slip-items { width: 100%; margin-top: 28px; border-collapse: collapse; }
        .slip-items th { text-align: left; font-size: 10px; letter-spacing: 2px; text-transform: uppercase;
                         color: #666; padding: 10px 8px; border-bottom: 1px solid #ddd; }
        .slip-items td { padding: 12px 8px; border-bottom: 1px solid #f0f0f0; font-size: 13px; }
        .slip-items td.qty, .slip-items td.money { text-align: right; }
        .slip-totals { display: flex; justify-content: flex-end; margin-top: 20px; }
        .slip-totals table { min-width: 260px; }
        .slip-totals td { padding: 4px 8px; font-size: 13px; }
        .slip-totals tr.grand td { font-size: 20px; font-weight: 700; border-top: 2px solid #111; padding-top: 10px; }
        .buyer-insights { margin-top: 28px; background: #FFF8E7; border: 1px solid #F5D66E; border-radius: 8px; padding: 18px 20px; }
        .buyer-insights h3 { margin: 0 0 8px 0; font-size: 14px; letter-spacing: 2px; text-transform: uppercase; color: #8a6600; }
        .buyer-insights .row { display: flex; gap: 6px; font-size: 13px; padding: 4px 0; }
        .buyer-insights .row strong { min-width: 140px; color: #333; }
        .buyer-insights .row span { color: #111; }
        .bday-badge { display: inline-block; background: #FF3B30; color: white; padding: 2px 8px; border-radius: 4px;
                      font-size: 11px; letter-spacing: 1px; text-transform: uppercase; margin-left: 6px; }
        .print-controls { max-width: 780px; margin: 0 auto 20px; display: flex; justify-content: space-between; align-items: center; }
        .print-btn { background: #FF3B30; color: white; border: 0; padding: 10px 18px; border-radius: 6px;
                     font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; }
        .footer-note { margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee;
                       font-size: 11px; color: #888; text-align: center; }
        @media print {
          .print-controls { display: none !important; }
          .slip-page { padding: 0; }
          /* Hide global site chrome so only the slip prints */
          body > div > header, body > div > footer, .glass-header { display: none !important; }
        }
      `}</style>

      <div className="print-controls">
        <div style={{ fontSize: 12, color: "#666" }}>
          Order #{order.id?.slice(0, 8)} · Card Lobster Packing Slip
        </div>
        <button onClick={() => window.print()} className="print-btn" data-testid="slip-print-btn">
          <Printer size={16} /> Print
        </button>
      </div>

      <div className="slip-max" data-testid="packing-slip">
        <div className="slip-header">
          <div>
            <div className="slip-brand">CARD<em>LOBSTER</em></div>
            <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#888", marginTop: 4 }}>
              Packing Slip
            </div>
          </div>
          <div className="slip-meta">
            <div>Order Date</div>
            <div className="oid">{order.created_at?.slice(0, 10)}</div>
            <div style={{ marginTop: 8 }}>Order #</div>
            <div className="oid">{order.id?.slice(0, 12)}</div>
            <div style={{ marginTop: 8 }}>Status</div>
            <div className="oid">{order.shipping_status || "pending"}</div>
          </div>
        </div>

        <div className="slip-grid">
          <div className="slip-box">
            <div className="slip-label">Ship To</div>
            {buyer ? (
              <>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{buyer.name}</div>
                <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{buyer.email}</div>
                {addr.line1 && (
                  <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.5 }}>
                    <div>{addr.line1}</div>
                    {addr.line2 && <div>{addr.line2}</div>}
                    <div>{[addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}</div>
                    <div>{addr.country}</div>
                  </div>
                )}
                {!addr.line1 && (
                  <div style={{ marginTop: 10, fontSize: 12, color: "#c00" }}>
                    No shipping address on file. Contact buyer before shipping.
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 12, color: "#c00" }}>Guest order — no buyer profile.</div>
            )}
          </div>

          <div className="slip-box">
            <div className="slip-label">From</div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Card Lobster · Lobster5</div>
            <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>cardlobster.com</div>
            {order.tracking && (
              <div style={{ marginTop: 10, fontSize: 12 }}>
                <div className="slip-label" style={{ marginBottom: 2 }}>Tracking</div>
                <div style={{ fontFamily: "monospace" }}>{order.tracking}</div>
              </div>
            )}
            <div style={{ marginTop: 10, fontSize: 12 }}>
              <div className="slip-label" style={{ marginBottom: 2 }}>Payment</div>
              <div>{order.payment_status || "pending"}</div>
            </div>
          </div>
        </div>

        <table className="slip-items">
          <thead>
            <tr>
              <th>Item</th>
              <th style={{ textAlign: "right" }}>Qty</th>
              <th style={{ textAlign: "right" }}>Price</th>
              <th style={{ textAlign: "right" }}>Line Total</th>
            </tr>
          </thead>
          <tbody>
            {order.items?.map((it, i) => (
              <tr key={i}>
                <td>{it.title}</td>
                <td className="qty">{it.quantity}</td>
                <td className="money">${it.price?.toFixed(2)}</td>
                <td className="money">${(it.price * it.quantity).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="slip-totals">
          <table>
            <tbody>
              <tr>
                <td style={{ color: "#666" }}>Subtotal</td>
                <td style={{ textAlign: "right" }}>${order.subtotal?.toFixed(2)}</td>
              </tr>
              {(order.discount || 0) > 0 && (
                <tr>
                  <td style={{ color: "#FF3B30" }}>Discount</td>
                  <td style={{ textAlign: "right", color: "#FF3B30" }}>−${order.discount?.toFixed(2)}</td>
                </tr>
              )}
              <tr className="grand">
                <td>Total</td>
                <td style={{ textAlign: "right" }}>${order.total?.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {buyer && (prefs.length > 0 || buyer.birthday) && (
          <div className="buyer-insights" data-testid="buyer-insights">
            <h3>
              Buyer Insights
              {buyer.birthday && <span className="bday-badge">🎂 {buyer.birthday}</span>}
            </h3>
            <p style={{ margin: "4px 0 12px 0", fontSize: 12, color: "#8a6600" }}>
              Add a personal touch — mention a favorite below in the thank-you note.
            </p>
            {prefs.map(([label, value]) => (
              <div key={label} className="row"><strong>{label}</strong><span>{value}</span></div>
            ))}
          </div>
        )}

        <div className="footer-note">
          Thanks for ripping with Card Lobster · Follow us on Whatnot &amp; TikTok · cardlobster.com
        </div>
      </div>
    </div>
  );
}
